import React, { useCallback, useEffect, useRef, useState } from "react";
import { hapticTap, hapticSelect } from "../../Cockpit/haptics";
import { formatWithSubscript } from "../../utils/formatter";

/**
 * The order confirmation dialog.
 *
 * Order placement is the highest-risk interaction on the platform, and it is
 * the one that was reachable with a single click on a button labelled "Market
 * Buy/Long". This is the dedicated confirmation layer that click now opens: a
 * modal that states the asset, the side, the size, the price it will execute
 * at, every fee, and the total cash impact — the things a trader cannot check
 * after the fact, because the position is already open.
 *
 * It is a centred dialog rather than the inline panel it replaces. An inline
 * summary sits inside the ticket it is summarising, so the controls that
 * produced it are still on screen and still look editable; a modal makes the
 * order the only thing on screen, which is the point of the step.
 *
 * Confirming is a hold, not a tap. A tap is the same gesture that changed the
 * leverage a moment ago and it can be produced by a mis-click; 700ms of
 * sustained contact cannot. Releasing early cancels, and nothing is sent until
 * the fill completes — so the fill doubles as the "not yet sent" indicator.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm - Fired once the hold completes
 * @param {0|1} props.side - 0 long, 1 short
 * @param {0|1} props.orderType - 0 market, 1 limit
 * @param {string} props.pair - e.g. "LA/USDC"
 * @param {string} [props.marketName]
 * @param {number} props.deposit
 * @param {number} props.leverage
 * @param {string|number} props.positionSize
 * @param {string|JSX.Element} props.price - Execution or limit price, preformatted
 *   (JSX for the subscript-zero notation low-price tokens use)
 * @param {string|JSX.Element} props.liquidationPrice - Preformatted
 * @param {number} [props.openingFee]
 * @param {number} [props.closingFee]
 * @param {boolean} [props.isLoadingFees]
 * @param {string|number} [props.dailyBorrowingRate] - Percent per day
 * @param {number|string} [props.slippage]
 * @param {boolean} [props.isAutoSlippage]
 * @param {number|string} [props.tp]
 * @param {number|string} [props.sl]
 * @param {number} [props.balance]
 * @param {(n: number|string) => string} props.formatNumber
 */
const HOLD_MS = 700;

const Row = ({ label, value, tone, hint }) => (
  <div className="flex items-start justify-between" style={{ gap: 12, padding: "5px 0" }}>
    <span style={{ fontFamily: "General Sans", fontSize: 12.5, color: "var(--ink-2, #667085)" }}>
      {label}
      {hint && (
        <span style={{ display: "block", fontSize: 11, color: "var(--ink-3, #98A2B3)" }}>{hint}</span>
      )}
    </span>
    <span
      style={{
        fontFamily: "General Sans",
        fontSize: 12.5,
        fontWeight: 600,
        textAlign: "right",
        color: tone || "var(--ink, #101828)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </span>
  </div>
);

const OrderConfirmModal = ({
  open,
  onClose,
  onConfirm,
  side,
  orderType,
  pair,
  marketName,
  deposit,
  leverage,
  positionSize,
  price,
  liquidationPrice,
  openingFee,
  closingFee,
  isLoadingFees,
  dailyBorrowingRate,
  slippage,
  isAutoSlippage,
  tp,
  sl,
  balance,
  formatNumber,
}) => {
  const [holdPct, setHoldPct] = useState(0);
  // "idle" → "holding" → "done", plus "slipped" for a hold released early.
  // The last one is the whole reason this is a state machine rather than a
  // number: a trader who lets go at 80% has done something the control should
  // acknowledge, or the gesture reads as broken rather than as cancelled.
  const [phase, setPhase] = useState("idle");
  const raf = useRef(null);
  const slipTimer = useRef(null);
  const long = side === 0;

  /**
   * The borrowing row's text.
   *
   * `dailyBorrowingRate` arrives as `{ dailyCost, dailyRatePct,
   * marketDailyRatePct, paysSide }`, so it cannot be interpolated directly —
   * doing so printed "[object Object]% / day" on every confirmation.
   */
  const borrowing = (() => {
    const r = dailyBorrowingRate;
    if (!r || typeof r !== "object") {
      // Older callers passed a bare number; keep them working.
      return { pays: true, text: r != null && r !== "" ? `${r}% / day` : "--" };
    }
    if (r.paysSide) {
      return { pays: true, text: `${Number(r.dailyRatePct).toFixed(4)}% / day` };
    }
    const market = Number(r.marketDailyRatePct) || 0;
    return { pays: false, text: market > 0 ? `0% / day (market ${market.toFixed(4)}%)` : "0% / day" };
  })();

  const cancelHold = useCallback(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;
    setPhase((prev) => {
      if (prev !== "holding") return prev;
      // Only worth saying if they had actually got somewhere.
      clearTimeout(slipTimer.current);
      slipTimer.current = setTimeout(() => setPhase("idle"), 900);
      return "slipped";
    });
    setHoldPct(0);
  }, []);

  const startHold = useCallback(() => {
    if (raf.current || phase === "done") return;
    clearTimeout(slipTimer.current);
    setPhase("holding");
    hapticTap();
    // performance.now, not Date.now: this is a duration, and a clock that can
    // step backwards would stall the fill mid-hold.
    const from = performance.now();
    const step = (now) => {
      const pct = Math.min(100, ((now - from) / HOLD_MS) * 100);
      setHoldPct(pct);
      if (pct >= 100) {
        raf.current = null;
        setPhase("done");
        hapticSelect();
        // A beat on "Confirmed" before the dialog goes. Sending on the exact
        // frame the fill completes gives the trader nothing that says the hold
        // was what did it — the modal simply vanishes mid-gesture.
        setTimeout(() => onConfirm?.(), 260);
        return;
      }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [onConfirm, phase]);

  useEffect(() => () => { clearTimeout(slipTimer.current); }, []);

  // Reset the hold gesture every time the dialog opens. This component
  // instance persists across separate order submissions (the parent just
  // toggles `open`), so without this a trader who confirmed one order and
  // then opened the ticket for a new one saw the previous order's "Confirmed
  // / Sending your order…" state instead of a fresh confirmation screen.
  useEffect(() => {
    if (open) {
      setPhase("idle");
      setHoldPct(0);
    }
  }, [open]);

  /* Escape closes, and the page behind does not scroll while the dialog is up
     — a confirmation the trader can scroll away from is not a stop. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      cancelHold();
    };
  }, [open, onClose, cancelHold]);

  // A nonzero fee that still rounds to $0.00 at 2 decimals (routine on a
  // $0.01 test deposit) gets the CoinGecko-style subscript treatment instead
  // of silently reading as "no fee" — the fee is real, just small.
  const formatFee = (value) => {
    const n = Number(value) || 0;
    if (n > 0 && Number(n.toFixed(2)) === 0) {
      return formatWithSubscript(n, { prefix: "$", sigDigits: 3 });
    }
    return `$${formatNumber(String(n))}`;
  };

  if (!open) return null;

  const fee = isLoadingFees ? 0 : Number(openingFee) || 0;
  const totalCost = (Number(deposit) || 0) + fee;
  const remaining = Math.max(0, (Number(balance) || 0) - totalCost);
  // The move against the position that wipes the deposit. Leverage as a
  // multiple is abstract; "a 7% move liquidates this" is the same fact stated
  // as the thing that actually happens.
  const liqMovePct = Number(leverage) > 1 ? (100 / Number(leverage)).toFixed(1) : null;
  const accent = long ? "#1F8B58" : "#C8353A";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm order"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 400,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "var(--scrim)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <style>{`
        @keyframes ceOcmIn { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
        .ce-ocm-card { animation: ceOcmIn 220ms cubic-bezier(.22,1,.36,1); }
        /* Every fill on this button is a background-image, and the
           background-COLOR is forced transparent.

           Both halves are load-bearing. A <button> with no author background
           falls back to the UA's buttonface, which under color-scheme: dark
           is an opaque grey — that is what was washing the gradient out to a
           flat mid-green. And the page-wide dark sweep in theme.css rewrites
           background declarations to surface tokens with !important, which
           is right for a hundred cards written as literal whites and wrong for
           a semantic fill whose colour *is* the state. A background-image
           survives the sweep; a background-color does not.

           appearance: none is what stops Safari painting its own control
           chrome over the top of both. */
        .ce-ocm-hold {
          -webkit-appearance: none;
          appearance: none;
          background-color: transparent !important;
          /* The confirm button is a 54px block, not a chip — it opts out of the
             page-wide "every control is a pill" rule the same way the ticket's
             submit does, so the two read as the same object. */
          border-radius: 16px !important;
          transition: transform var(--dur-fast, 140ms) var(--ease, ease),
                      box-shadow var(--dur-base, 220ms) var(--ease, ease),
                      background-image var(--dur-base, 220ms) var(--ease, ease);
        }

        /* The four states, each with its own COLOURED shadow — the same rule
           the rest of the restyle follows: a filled control's shadow is tinted
           to its fill, never grey. */
        .ce-ocm-hold.is-long  { background-image: linear-gradient(180deg, #24A066 0%, #1F8B58 100%); box-shadow: 0 10px 24px rgba(31,139,88,.32), inset 0 1px 0 rgba(255,255,255,.28); }
        .ce-ocm-hold.is-short { background-image: linear-gradient(180deg, #D93F44 0%, #C8353A 100%); box-shadow: 0 10px 24px rgba(200,53,58,.32), inset 0 1px 0 rgba(255,255,255,.28); }

        .ce-ocm-hold.is-long:hover  { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(31,139,88,.34), inset 0 1px 0 rgba(255,255,255,.28); }
        .ce-ocm-hold.is-short:hover { transform: translateY(-1px); box-shadow: 0 14px 30px rgba(200,53,58,.34), inset 0 1px 0 rgba(255,255,255,.28); }

        /* The press goes down — this control is held, so it has to stay
           visibly depressed for the whole gesture rather than flashing. */
        .ce-ocm-hold:active { transform: translateY(1px) scale(.985); box-shadow: inset 0 2px 8px rgba(0,0,0,.22); }

        /* Released early. Amber rather than a dimmed version of the direction
           colour: "you let go" is not a weaker form of "you bought", and a
           faded green here read as the button having half-committed. */
        .ce-ocm-hold.is-slipped,
        .ce-ocm-hold.is-slipped:hover {
          background-image: linear-gradient(180deg, #C7913A 0%, #A8761A 100%);
          box-shadow: 0 8px 18px rgba(168,118,26,.30), inset 0 1px 0 rgba(255,255,255,.24);
          transform: none;
        }

        /* Committed. Both directions land on the same green, because the
           statement is "this went through", not "this was a buy". */
        .ce-ocm-hold.is-done,
        .ce-ocm-hold.is-done:hover,
        .ce-ocm-hold.is-done:active {
          background-image: linear-gradient(180deg, #24A066 0%, #1F8B58 100%);
          box-shadow: 0 10px 24px rgba(31,139,88,.32), inset 0 1px 0 rgba(255,255,255,.28);
          animation: ceOcmDone .26s ease-out;
          transform: none;
          cursor: default;
        }

        .ce-ocm-hold:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(255,255,255,.35), 0 0 0 6px rgba(0,0,0,.25); }
        @keyframes ceOcmDone { 0% { transform: scale(1); } 45% { transform: scale(1.02); } 100% { transform: scale(1); } }
        @media (prefers-reduced-motion: reduce) {
          .ce-ocm-card, .ce-ocm-hold.is-done { animation: none; }
        }
      `}</style>

      <div
        className="ce-ocm-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 400,
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 22,
          background: "var(--surface, #fff)",
          border: 0,
          boxShadow: "var(--float)",
          padding: 18,
        }}
      >
        {/* Identity first: which side, of which market, as which order type.
            All three are one line because they are one sentence. */}
        <div className="flex items-start justify-between" style={{ gap: 10, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div
              className="flex items-center"
              style={{ gap: 6, fontFamily: "General Sans", fontWeight: 700, fontSize: 17, color: accent }}
            >
              <span aria-hidden="true">{long ? "↑" : "↓"}</span>
              {long ? "Buy / Long" : "Sell / Short"}
            </div>
            <div style={{ fontFamily: "General Sans", fontSize: 12.5, color: "var(--ink-2, #667085)", marginTop: 2 }}>
              {marketName ? `${marketName} · ` : ""}{pair} · {orderType === 0 ? "Market order" : "Limit order"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
              border: 0, background: "var(--raise)", boxShadow: "var(--pillow-sm)",
              color: "var(--ink-2, #667085)", cursor: "pointer", fontSize: 13, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Size is the headline. It is the number that decides how much a move
            in this market is worth, and it is not the number the trader typed. */}
        <div
          style={{
            borderRadius: 16,
            padding: "12px 14px",
            background: "var(--cat-accent-soft, rgba(91,29,238,.10))",
            marginBottom: 12,
          }}
        >
          <div style={{ fontFamily: "General Sans", fontSize: 11.5, color: "var(--ink-2, #667085)" }}>
            Position size
          </div>
          <div style={{ fontFamily: "General Sans", fontWeight: 700, fontSize: 24, color: "var(--ink, #101828)", lineHeight: 1.2 }}>
            ${formatNumber(positionSize)}
          </div>
          <div style={{ fontFamily: "General Sans", fontSize: 11.5, color: "var(--ink-2, #667085)" }}>
            ${formatNumber(String(deposit))} deposit × {Math.round(Number(leverage) || 1)}× leverage
          </div>
        </div>

        <div className="ce-modal-panel">
          <Row label={orderType === 0 ? "Execution price" : "Limit price"} value={<>${price}</>} />
          <Row
            label="Liquidation price"
            tone={liquidationPrice && Number(leverage) > 1 ? "#C8353A" : undefined}
            hint={liqMovePct ? `a ${liqMovePct}% move against you` : undefined}
            value={Number(leverage) > 1 ? <><span aria-hidden="true">⚠ </span>${liquidationPrice}</> : "None"}
          />
          {tp ? <Row label="Take profit" value={`$${formatNumber(String(tp))}`} tone="#1F8B58" /> : null}
          {sl ? <Row label="Stop loss" value={`$${formatNumber(String(sl))}`} tone="#C8353A" /> : null}
          <Row
            label="Max slippage"
            value={slippage != null ? `${slippage}%${isAutoSlippage ? " (auto)" : ""}` : "--"}
          />
        </div>

        {/* Costs, all of them, in one block. Fees disclosed at the moment of
            commitment rather than discovered afterwards on the position. */}
        <div className="ce-modal-panel" style={{ marginTop: 8 }}>
          <Row label="Opening fee" value={isLoadingFees ? "…" : formatFee(fee)} />
          <Row
            label="Closing fee"
            hint="charged when you close"
            value={isLoadingFees ? "…" : formatFee(Number(closingFee) || 0)}
          />
          {/* `dailyBorrowingRate` is the object TradingForm computes, not a
              number — interpolating it printed "[object Object]% / day". Only
              the dominant-OI side pays, so when this trader's side does not,
              the row says so and quotes the market rate as context, the same
              way `EstimatedSlippage` does. */}
          <Row
            label="Borrowing"
            hint={
              borrowing.pays
                ? "accrues while the position is open"
                : "your side is not paying at the current OI"
            }
            value={borrowing.text}
          />
        </div>

        <div
          className="flex items-center justify-between"
          style={{ borderTop: "1px solid var(--line, #E3E3E3)", marginTop: 4, paddingTop: 10, gap: 12 }}
        >
          <div>
            <div style={{ fontFamily: "General Sans", fontSize: 13.5, fontWeight: 600, color: "var(--ink, #101828)" }}>
              Total cost
            </div>
            <div style={{ fontFamily: "General Sans", fontSize: 11.5, color: "var(--ink-2, #667085)" }}>
              Balance after: ${formatNumber(String(remaining))}
            </div>
          </div>
          <span style={{ fontFamily: "General Sans", fontSize: 18, fontWeight: 700, color: "var(--ink, #101828)", fontVariantNumeric: "tabular-nums" }}>
            ${formatNumber(String(totalCost))}
          </span>
        </div>

        {/* Hold to confirm.
            The gesture is deliberate friction, so the control has to teach it
            and then report on it: the label says what to do before the press,
            the fill and the ring say how far the hold has got, the leading
            edge tracks the fill so the motion has a head, and letting go early
            says so rather than silently resetting. Keyboard gets the same
            gesture on Space/Enter — a confirmation reachable only by pointer
            is a confirmation some traders cannot give. */}
        <button
          type="button"
          className={`ce-ocm-hold ${long ? "is-long" : "is-short"}${phase === "done" ? " is-done" : ""}${phase === "slipped" ? " is-slipped" : ""}`}
          aria-label={`Press and hold to ${long ? "buy" : "sell"}`}
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          onPointerCancel={cancelHold}
          onKeyDown={(e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); startHold(); } }}
          onKeyUp={(e) => { if (e.key === " " || e.key === "Enter") cancelHold(); }}
          onBlur={cancelHold}
          style={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            height: 54,
            marginTop: 14,
            borderRadius: 16,
            border: "none",
            cursor: "pointer",
            fontFamily: "General Sans",
            fontWeight: 600,
            fontSize: 15,
            color: "#fff",
            touchAction: "none",
            userSelect: "none",
            WebkitTapHighlightColor: "transparent",
            ...(phase === "holding" ? { transform: "scale(.988)" } : null),
          }}
        >
          {/* Travelled region. */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute", top: 0, bottom: 0, left: 0,
              width: `${holdPct}%`,
              background: "rgba(255,255,255,.26)",
              // Snaps back instantly on a slip, so the reset reads as a
              // release rather than as the bar draining on its own.
              transition: phase === "holding" ? "none" : "width .18s ease",
              pointerEvents: "none",
            }}
          />
          {/* The leading edge: a bright 2px head with a glow, which is what
              makes the fill read as travelling rather than as growing. */}
          {holdPct > 0 && holdPct < 100 && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute", top: 0, bottom: 0,
                left: `${holdPct}%`,
                width: 2,
                background: "rgba(255,255,255,.9)",
                boxShadow: "0 0 12px 2px rgba(255,255,255,.55)",
                pointerEvents: "none",
              }}
            />
          )}

          <span className="flex items-center justify-center" style={{ position: "relative", gap: 9 }}>
            {phase === "done" ? (
              <>
                <span aria-hidden="true">✓</span>
                Confirmed
              </>
            ) : phase === "slipped" ? (
              "Released — hold again"
            ) : phase === "holding" ? (
              <>
                {/* A ring that closes as the hold completes. The bar behind is
                    read peripherally; the ring is what a trader watching the
                    button itself sees fill. */}
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="9" cy="9" r="7.5" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="2.5" />
                  <circle
                    cx="9" cy="9" r="7.5" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 7.5}
                    strokeDashoffset={2 * Math.PI * 7.5 * (1 - holdPct / 100)}
                  />
                </svg>
                Keep holding…
              </>
            ) : (
              <>
                <span aria-hidden="true" style={{ fontSize: 13 }}>◉</span>
                Hold to {long ? "buy" : "sell"}
              </>
            )}
          </span>
        </button>

        {/* Said once, under the control, rather than left to be discovered by
            tapping and having nothing happen. */}
        <div
          aria-live="polite"
          style={{
            marginTop: 6,
            textAlign: "center",
            fontFamily: "General Sans",
            fontSize: 11.5,
            color: "var(--ink-3, #98A2B3)",
          }}
        >
          {phase === "done"
            ? "Sending your order…"
            : `Press and hold for ${(HOLD_MS / 1000).toFixed(1)}s — release to cancel`}
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 8,
            padding: "8px 0",
            background: "none",
            border: "none",
            cursor: "pointer",
            fontFamily: "General Sans",
            fontSize: 12.5,
            color: "var(--ink-2, #667085)",
          }}
        >
          Back to edit
        </button>
      </div>
    </div>
  );
};

export default OrderConfirmModal;
